import { useAccessTokenState } from "@/hooks";
import type {
  PlatformData,
  SocialEngagementData,
  SocialPlatform,
} from "@/types/social-engagement";
import { useCallback, useState } from "react";
import useSWR from "swr";
import type { SocialEngagementModalState } from "./modalConfig";

const socialEngagementFetcher = async (url: string, accessToken: string) => {
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to analyze social engagement");
  }

  return response.json();
};

interface UseSocialEngagementAnalysisReturn {
  data: SocialEngagementData;
  modalState: SocialEngagementModalState;
  error: string | null;
  isLoading: boolean;
  startAnalysis: () => void;
  retryAnalysis: () => void;
  userLeaderboardRank?: number;
}

export const useSocialEngagementAnalysis = (
  gameUUID: string | undefined,
): UseSocialEngagementAnalysisReturn => {
  const [modalState, setModalState] =
    useState<SocialEngagementModalState>("analyzing");
  const [shouldFetch, setShouldFetch] = useState(false);
  const accessToken = useAccessTokenState();

  const key = shouldFetch ? `/api/social-engagement?game_id=${gameUUID}` : null;
  const {
    data: swrData,
    error: swrError,
    isLoading,
    mutate,
  } = useSWR<{
    success: boolean;
    data?: SocialEngagementData;
    error?: string;
  }>(
    accessToken && key ? key : null,
    (path: string) => socialEngagementFetcher(path, accessToken as string),
    {
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      onSuccess: () => {
        setModalState("points_summary");
      },
      onError: () => {
        setModalState("error");
      },
    },
  );

  const data: SocialEngagementData = swrData?.data || {
    platforms: {},
    totalPoints: 0,
  };
  const error: string | null = swrError?.message || null;

  const effectiveModalState = isLoading ? "analyzing" : modalState;

  const startAnalysis = useCallback(() => {
    setModalState("analyzing");
    setShouldFetch(true);
  }, []);

  const retryAnalysis = useCallback(() => {
    setModalState("analyzing");
    setShouldFetch(true);
    mutate();
  }, [mutate]);

  return {
    data,
    modalState: effectiveModalState,
    error,
    isLoading,
    startAnalysis,
    retryAnalysis,
  };
};

export type {
  SocialPlatform,
  PlatformData,
  SocialEngagementData,
  SocialEngagementModalState,
};
